import { Injectable } from '@angular/core';
import { SortDirection } from '@wb/core/enums/sort-direction';
import { SharedImportProfile } from '@wb/core/interfaces/shared-import-profile';
import { gql, Query } from 'apollo-angular';

export interface ModeSignedUrlResponse {
  modeSignedUrl: {
    code: string;
    success: boolean;
    message: string;
    modeSignedUrl: ModeSignedUrl;
  };
}

export interface ModeSignedUrl {
  signature?: string;
  signedUrl: string;
  accessKey?: string;
  timestamp?: number;
}

export interface ModeSignedUrlInput {
  input: {
    url: string;
    key: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ModeSignedUrlQuery extends Query<ModeSignedUrlResponse, ModeSignedUrlInput> {
  document = gql(`
  query modeSignedUrl($input: ModelModeSignedUrlInput) {
    modeSignedUrl(input: $input) {
      code
      success
      message
      modeSignedUrl {
        signature
        signedUrl
        accessKey
        timestamp
      }
    }
  }
  `);
}

export interface WidgetsResponse {
  widgets: {
    code: string;
    success: boolean;
    message: string;
    widgets: Widget[];
  };
}

export interface Widget {
  uid: string;
  name: string;
  description: string;
  url: string;
  type: string;
}

export interface WidgetFilter {
  uid?: string;
  type?: string;
}

export interface WidgetsInput {
  where?: WidgetFilter;
  limit?: number;
  offset?: number;
  sortDirection?: SortDirection;
}

@Injectable({
  providedIn: 'root',
})
export class WidgetsQuery extends Query<WidgetsResponse, WidgetsInput> {
  document = gql(`
  query widgets($where: ModelWidgetsFilterInput) {
    widgets(where: $where) {
      code
      success
      message
      widgets {
        uid
        name
        description
        url
        type
      }
    }
  }
  `);
}

export interface SharedImportProfilesResponse {
  sharedImportProfiles: {
    code: string;
    success: boolean;
    message: string;
    sharedImportProfiles: SharedImportProfile[];
  };
}

export interface SharedImportProfilesParams {
  limit?: number;
}

@Injectable({
  providedIn: 'root',
})
export class SharedImportProfilesGQL extends Query<SharedImportProfilesResponse, SharedImportProfilesParams> {
  document = gql(`
    query sharedImportProfiles ($where: ModelSharedImportProfileFilterInput, $limit: Int, $offset: Int, $sortDirection: ModelSortDirection) {
      sharedImportProfiles (where: $where, limit: $limit, offset: $offset, sortDirection: $sortDirection) {
        success
        message
        code
        sharedImportProfiles {
          uid
          ftp_user
          type
          importProfileUid
          importProfileName
          importProfileUploadsType
          importProfileUploadsCreatedAt
          importProfileUploadsProcessedAt
        }
      }
    }
  `);
}

export interface GetSecretResponse {
  getSecret: {
    code: String;
    success: Boolean;
    message: String;
    secret: any;
  };
}

@Injectable({
  providedIn: 'root',
})
export class GetSecretGQL extends Query<GetSecretResponse> {
  document = gql(`
    query getSecret($username: String, $protocol: String) {
      getSecret(username: $username, protocol: $protocol) {
        code
        message
        secret {
          userName
          password
        }
      }
    }
  `);
}

export interface GetGlobalSettingResponse {
  globalSettingValue: {
    code: String;
    success: Boolean;
    globalSettingValue: any;
  };
}

@Injectable({
  providedIn: 'root',
})
export class GetGlobalSettingGQL extends Query<GetGlobalSettingResponse> {
  document = gql(`
    query getGlobalSettingValue($key: String!) {
      globalSettingValue(key: $key) {
        code
        success
        globalSettingValue {
          value
        }
      }
    }
  `);
}

export interface ClientReportResponse {
  clientReport: {
    code: String;
    success: Boolean;
    clientReport: String;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ClientListReport extends Query<ClientReportResponse> {
  document = gql(`
    query clientReport(
      $accountUids: String!
      $startDate: String
      $endDate: String
      $isAggregator: Boolean
    ) {
      clientReport(
        input: {
          accountUids: $accountUids
          startDate: $startDate
          endDate: $endDate
          isAggregator: $isAggregator
        }
      ) {
        code
        success
        clientReport
      }
    }
  `);
}

export interface SessionStatusResponse {
  sessionStatus: {
    code: string;
    success: boolean;
    message: string;
    sessionStatuses: SessionStatus[];
    totalCount: number;
  };
}

export interface SessionStatus {
  account: string;
  uid: string;
  latestQualifiedLead: string;
  latestSession: string;
}

@Injectable({
  providedIn: 'root',
})
export class SessionStatusGQL extends Query<SessionStatusResponse, {}> {
  document = gql(`
    query sessionStatus($userUid: ID!, $limit: Int, $offset: Int, $sortDirection: ModelSortDirection, $sortColumn: String, $search: String) {
      sessionStatus(userUid: $userUid, limit: $limit, offset: $offset, sortDirection: $sortDirection, sortColumn: $sortColumn, search: $search) {
        code
        success
        message
        sessionStatuses {
          account
          uid
          latestQualifiedLead
          latestSession
        }
        totalCount
      }
    }
  `);
}

export interface InventoryFeedStatusResponse {
  inventoryFeedStatus: {
    code: string;
    success: boolean;
    message: string;
    inventoryFeedStatuses: InventoryFeedStatus[];
    totalCount: number;
  };
}

export interface InventoryFeedStatus {
  account: string;
  accountUid: string;
  importProfile: string;
  protocol: string;
  latestProcessedAt: string;
}

@Injectable({
  providedIn: 'root',
})
export class InventoryFeedStatusGQL extends Query<InventoryFeedStatusResponse, {}> {
  document = gql(`
    query inventoryFeedStatus($userUid: ID!, $limit: Int, $offset: Int, $sortDirection: ModelSortDirection, $sortColumn: String, $search: String) {
      inventoryFeedStatus(userUid: $userUid, limit: $limit, offset: $offset, sortDirection: $sortDirection, sortColumn: $sortColumn, search: $search) {
        code
        success
        message
        inventoryFeedStatuses {
          account
          accountUid
          protocol
          importProfile
          latestProcessedAt
        }
        totalCount
      }
    }
  `);
}
